import { OpenInNewRounded } from '@mui/icons-material';
import { Box, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, styled } from '@mui/material';
import { useAppSelector } from 'app/config/store';
import { getDelayedInspections, reset } from 'app/entities/inspection-history/inspection-history.reducer';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { IInspectionDelayed } from 'app/shared/model/inspection-delayed.model';
import { showElement } from 'app/shared/util/data-utils';
import { convertDateTimeToPtBR } from 'app/shared/util/date-utils';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import React, { useCallback, useEffect, useState } from 'react';
import { Translate, getSortState } from 'react-jhipster';
import { useDispatch } from 'react-redux';
import { Link, RouteComponentProps } from 'react-router-dom';

const headers: IHeaderItem[] = [
  { key: 'licensePlate', sortable: false, component: <Translate contentKey="fleetSenseApp.inspectionHistory.truckImplement" /> },
  { key: 'inspectionName', sortable: false, component: <Translate contentKey="fleetSenseApp.inspectionHistory.lastInspection" /> },
  { key: 'created', sortable: false, component: <Translate contentKey="fleetSenseApp.inspectionHistory.lastCreatedAt" /> },
];

const data: IListData[] = [
  { type: 'text', value: 'licensePlate' },
  { type: 'text', value: 'inspectionName' },
  { type: 'child', value: '' },
];

export const InspectionDelayed = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useDispatch();

  const [pagination, setPagination] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'created'), props.location.search)
  );

  const inspectionDelayed = useAppSelector(state => state.inspectionHistory.entitiesAlt);
  const loading = useAppSelector(state => state.inspectionHistory.loading);
  const totalItems = useAppSelector(state => state.inspectionHistory.totalItems);

  const getAllEntities = useCallback(() => {
    dispatch(reset());
    dispatch(
      getDelayedInspections({
        page: pagination.activePage - 1,
        size: pagination.itemsPerPage,
        sort: `${pagination.sort},${pagination.order}`,
      })
    );
  }, [pagination]);

  const sortEntities = () => {
    const endURL = `?page=${pagination.activePage}&sort=${pagination.sort},${pagination.order}`;
    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [pagination.activePage, pagination.order, pagination.sort]);

  const sort = p => () =>
    setPagination({
      ...pagination,
      order: pagination.order === ASC ? DESC : ASC,
      sort: p,
    });

  const handlePagination = (_, page) =>
    setPagination({
      ...pagination,
      activePage: page,
    });

  const setActivePage = () => {
    setPagination({
      ...pagination,
      activePage: 1,
    });
  };

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  const renderCreatedChip = (item: IInspectionDelayed) => (
    <Chip
      label={item.created ? convertDateTimeToPtBR(item.created) : 'Não realizada'}
      color={item.created ? 'warning' : 'error'}
      sx={{
        width: '120px',
      }}
    />
  );

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', paddingTop: '16px', textAlign: 'center', padding: '24px' }}>
      <PageHeader
        headingId="fleetSenseApp.inspectionHistory.home.delayed"
        headingDataCy="TruckImplementHeading"
        contentKey="fleetSenseApp.inspectionHistory.home.delayed"
        buttonTextKey="inspectionHistory.inspectionDelayed.createLabel"
        showButton={false}
        match={props.match}
      />
      {!loading && !!inspectionDelayed?.length && (
        <TableContainer sx={{ mt: 1 }}>
          <Table aria-label="inspection history table">
            <TableHead>
              <TableRow>
                {headers.map((header, i) => (
                  <TableCell
                    key={`entity-${i}`}
                    sx={{
                      borderBottom: 'none',
                      paddingBottom: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        alignItems: 'center',
                      }}
                    >
                      {header.component}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell sx={{ borderBottom: '0' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {inspectionDelayed.map((item: IInspectionDelayed, index) => (
                <TableRow key={index}>
                  <CustomBodyTableCell>
                    {item.licensePlate}
                    <IconButton color="secondary" component={Link} to={`dashboards/truck-implement/${item.truckImplementId}`}>
                      <OpenInNewRounded fontSize="small" />
                    </IconButton>
                  </CustomBodyTableCell>
                  <CustomBodyTableCell>
                    {item.inspectionName || '-'}
                    <IconButton
                      color="secondary"
                      component={Link}
                      to={`inspection-history?inspectionHistoryId=${encodeURIComponent(item.inspectionId)}`}
                      style={showElement(!!item.inspectionName)}
                    >
                      <OpenInNewRounded fontSize="small" />
                    </IconButton>
                  </CustomBodyTableCell>
                  <CustomBodyTableCell>{renderCreatedChip(item)}</CustomBodyTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!loading && !inspectionDelayed.length && <NoData title="inspeção" />}
      {loading && <Loading />}
      <PaginationComponent paginationState={pagination} totalItems={totalItems} onChange={handlePagination} list={inspectionDelayed} />
    </Box>
  );
};

const CustomBodyTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: '8px solid',
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));
